import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { englishCatalog, loadCatalog } from './catalog';
import {
  WIZARD_LANGUAGES_ENABLED,
  languageFromTag,
  readLaunchLangTag,
  readStoredLanguage,
  writeStoredLanguage,
} from './feature';
import { translate, type TranslateVars } from './translate';
import {
  getDirection,
  getLanguageMeta,
  normalizeStoredLanguage,
  type LanguageCode,
  type MessageCatalog,
  type TextDirection,
} from './types';

export type TranslateFn = (key: string, vars?: TranslateVars) => string;

interface I18nContextValue {
  /** Manual pick, or the Genesys language, or English. Not yet limited to this screen. */
  language: LanguageCode;
  /** Saved manual pick. Null when the user has never chosen. */
  manual: LanguageCode | null;
  catalog: MessageCatalog;
  /** Saves the choice and uses it from now on. */
  setLanguage: (code: LanguageCode) => void;
  /** Launch tag learned after the OAuth redirect, if the first read missed it. */
  reportLangTag: (tag: string) => void;
  languagesEnabled: boolean;
  /** Passed to Insights. Null when languages are off or the user never picked. */
  uiLang: LanguageCode | null;
  t: TranslateFn;
  dir: TextDirection;
  locale: string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function chosenLanguage(manual: LanguageCode | null, langTag: string, enabled: boolean): LanguageCode {
  if (!enabled) return 'en';
  return manual ?? languageFromTag(langTag);
}

/**
 * Holds the chosen language and redraws when it changes.
 * App applies the screen rule (install steps stay English) on top of this.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const languagesEnabled = WIZARD_LANGUAGES_ENABLED;
  const [manual, setManual] = useState<LanguageCode | null>(() => normalizeStoredLanguage(readStoredLanguage()));
  const [langTag, setLangTag] = useState(readLaunchLangTag);
  const language = chosenLanguage(manual, langTag, languagesEnabled);
  const [catalog, setCatalog] = useState<MessageCatalog>(englishCatalog);
  const requested = useRef(language);

  useEffect(() => {
    requested.current = language;
    let cancelled = false;
    void loadCatalog(language).then((loaded) => {
      if (cancelled || requested.current !== language) return;
      setCatalog(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [language]);

  const setLanguage = useCallback((code: LanguageCode) => {
    writeStoredLanguage(code);
    setManual(code);
  }, []);

  const reportLangTag = useCallback((tag: string) => {
    setLangTag((current) => (current === tag ? current : tag));
  }, []);

  const t = useCallback<TranslateFn>(
    (key, vars) => translate(catalog, englishCatalog, key, vars),
    [catalog],
  );

  const value = useMemo<I18nContextValue>(() => ({
    language,
    manual,
    catalog,
    setLanguage,
    reportLangTag,
    languagesEnabled,
    uiLang: languagesEnabled ? manual : null,
    t,
    dir: getDirection(language),
    locale: getLanguageMeta(language).locale,
  }), [language, manual, catalog, setLanguage, reportLangTag, languagesEnabled, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
